import type { DetailUnavailableReason } from '@/api/competitionDetail'

import { REASON_TEXT as LIST_REASON_TEXT } from '../competitions/columns'

/*
 * How the page words a figure the API could not give (its `not_available`): the
 * competitions list's sentence for the list's own reasons, so the summary reads as the
 * list does, and the page's own for the rest, each under the label `NOT_AVAILABLE` gives
 * it.
 */

export const DETAIL_REASON_TEXT: Record<DetailUnavailableReason, string> = {
  ...LIST_REASON_TEXT,
  not_per_competition: 'The events that count a visit name no competition, so visitors cannot be counted per competition.',
  no_spend_per_competition:
    'No spend per competition: a campaign can promote several competitions, and nothing says how its spend divides between them. The Advertising and Channel performance pages report spend per campaign and channel.',
  no_price_history: 'The Suite keeps the current ticket price only, with no history of changes.',
  not_on_sale_in_range: 'It was not on sale on any complete day of this period.',
  no_capacity: 'It has no ticket capacity, or no lifetime tickets-sold figure, to pace against.',
  no_schedule: 'It has no start or no end date to pace between.',
  not_on_sale: 'It is not on sale now: it has not started, or it has ended.',
}

/**
 * The reasons that read "Not available" (the brief's words for them): a schedule figure
 * with no meaning for this competition now, and price changes, which the Suite never
 * keeps. Every other reason is "Not live yet" — a figure waiting on a source, or a funnel
 * stage the competition's events cannot measure.
 */
const NOT_AVAILABLE: ReadonlySet<DetailUnavailableReason> = new Set([
  'not_on_sale_in_range',
  'no_capacity',
  'no_schedule',
  'not_on_sale',
  'no_price_history',
])

/** "Not live yet" or "Not available", and why. */
export function describeDetailReason(reason: DetailUnavailableReason): { label: string; why: string } {
  return { label: NOT_AVAILABLE.has(reason) ? 'Not available' : 'Not live yet', why: DETAIL_REASON_TEXT[reason] }
}

/** A figure the API could not give, inline: its label, and why on hover and for a screen reader. */
export function Reasoned({ reason }: { reason: DetailUnavailableReason }) {
  const { label, why } = describeDetailReason(reason)
  return (
    <span className="cd-reasoned" title={why}>
      {label}
      <span className="ci-visually-hidden">. {why}</span>
    </span>
  )
}

/** "—": a figure undefined here (no orders, so no AOV), never a 0. */
export const UNDEFINED_FIGURE = '—'
